import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { CreateEventModal } from './CreateEventModal';
import {
  EVENT_DRAFT,
  EVENT_CONFIRMED,
  EVENT_COMPLETED,
  EVENT_CANCELLED,
  EVENT_ARCHIVED,
} from '../../lib/constants';

const STATUS_FILTERS = [
  { key: 'all',            labelKey: 'events.filterAll' },
  { key: EVENT_DRAFT,      labelKey: 'events.filterDraft' },
  { key: EVENT_CONFIRMED,  labelKey: 'events.filterConfirmed' },
  { key: EVENT_COMPLETED,  labelKey: 'events.filterCompleted' },
  { key: EVENT_CANCELLED,  labelKey: 'events.filterCancelled' },
  { key: EVENT_ARCHIVED,   labelKey: 'events.filterArchived' },
];

// Fallback used when the Worker is unreachable in dev. Same shape as the API.
const MOCK_EVENTS = [
  { id: 'a1b2c3d4e5f60001', name: 'Heritage Summit Kraków',     status: EVENT_CONFIRMED, eventDate: '2026-06-12', location: 'ICE Kraków',     mediaCount: 24, draftCount: 8 },
  { id: 'a1b2c3d4e5f60002', name: 'Youth Workshop Warsaw',      status: EVENT_DRAFT,     eventDate: '2026-09-20', location: 'Centrum Nauki', mediaCount: 0,  draftCount: 0 },
  { id: 'a1b2c3d4e5f60003', name: 'UNESCO Partner Meeting',     status: EVENT_COMPLETED, eventDate: '2026-05-03', location: 'Online',         mediaCount: 42, draftCount: 15 },
  { id: 'a1b2c3d4e5f60004', name: 'Sustainability Forum Gdańsk',status: EVENT_DRAFT,     eventDate: '2026-11-15', location: 'ECS Gdańsk',     mediaCount: 0,  draftCount: 0 },
];

function formatDate(eventDate, language) {
  const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
  // Append T00:00:00 so JS parses 'YYYY-MM-DD' as local midnight rather than
  // UTC midnight (which would shift the displayed day in non-UTC timezones).
  return new Date(`${eventDate}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isInCurrentMonth(eventDate) {
  if (!eventDate) return false;
  const d = new Date(`${eventDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

export function EventsList() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [draftsPending, setDraftsPending] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filtered fetch — drives the events grid. Re-runs on filter change or refresh.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const filters = selectedStatus === 'all' ? {} : { status: selectedStatus };
    api.events
      .list(filters)
      .then((data) => {
        if (cancelled) return;
        setEvents(data?.events ?? []);
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[events] API unreachable, falling back to mock data:', err.message);
        setEvents(
          selectedStatus === 'all'
            ? MOCK_EVENTS
            : MOCK_EVENTS.filter((e) => e.status === selectedStatus),
        );
        setUsingMockData(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedStatus, refreshKey]);

  // Unfiltered fetch — drives the stats counters. Re-runs only on refresh.
  useEffect(() => {
    let cancelled = false;
    api.events
      .list()
      .then((data) => {
        if (!cancelled) setAllEvents(data?.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setAllEvents(MOCK_EVENTS);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Drafts-pending count for the stats card.
  useEffect(() => {
    let cancelled = false;
    api.drafts
      .list({ status: 'pending_approval' })
      .then((data) => {
        if (!cancelled) setDraftsPending(data?.drafts?.length ?? 0);
      })
      .catch(() => {
        if (!cancelled) setDraftsPending(0);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const stats = useMemo(() => {
    const eventsThisMonth = allEvents.filter((e) => isInCurrentMonth(e.eventDate)).length;
    return [
      { icon: '📅', value: eventsThisMonth, labelKey: 'events.stats.eventsThisMonth' },
      { icon: '✏️', value: draftsPending,   labelKey: 'events.stats.draftsPending' },
      // TODO(api): wire to published-this-week count once a publish endpoint exists.
      { icon: '🚀', value: 0,               labelKey: 'events.stats.postsThisWeek' },
    ];
  }, [allEvents, draftsPending]);

  function handleCreated(created) {
    // Drop the user straight onto the new event so they can fill in details,
    // upload media, and create drafts without an extra click. The grid rebuilds
    // from a fresh fetch on next mount, so we don't need to bump refreshKey.
    if (created?.id) {
      navigate(`/events/${created.id}`);
      return;
    }
    setRefreshKey((k) => k + 1);
  }

  return (
    <div>
      <section className="stats-row" aria-label={t('events.title')}>
        {stats.map((stat) => (
          <div className="stat-card" key={stat.labelKey}>
            <div className="stat-card__icon" aria-hidden="true">{stat.icon}</div>
            <div className="stat-card__body">
              <div className="stat-card__number">{stat.value}</div>
              <div className="stat-card__label">{t(stat.labelKey)}</div>
            </div>
          </div>
        ))}
      </section>

      <div className="dashboard__section-header">
        <h1 className="dashboard__title">{t('events.title')}</h1>
      </div>

      {usingMockData && <div className="dev-notice">{t('events.loadError')}</div>}

      <div className="filter-bar">
        <div className="filter-pills" role="tablist" aria-label={t('events.title')}>
          {STATUS_FILTERS.map((filter) => {
            const isActive = selectedStatus === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={'filter-pill' + (isActive ? ' filter-pill--active' : '')}
                onClick={() => setSelectedStatus(filter.key)}
              >
                {t(filter.labelKey)}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="button button--primary"
          onClick={() => setShowCreateModal(true)}
        >
          + {t('events.newEvent')}
        </button>
      </div>

      {isLoading ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">⏳</div>
          <p>{t('events.loading')}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">📭</div>
          <h2>{t('events.noResults')}</h2>
          <p>{t('events.noResultsHint')}</p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => setShowCreateModal(true)}
          >
            {t('events.createFirst')}
          </button>
        </div>
      ) : (
        <div className="events-grid">
          {events.map((event) => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              className="event-card"
              aria-label={event.name}
            >
              <div className="event-card__header">
                <span className={`status-badge status-badge--${event.status}`}>
                  {t(`events.statuses.${event.status}`)}
                </span>
                <span className="event-card__date">
                  {event.eventDate
                    ? formatDate(event.eventDate, language)
                    : t('events.noDate')}
                </span>
              </div>

              <h3 className="event-card__name">{event.name}</h3>

              <div className="event-card__location">
                <span aria-hidden="true">📍</span>
                <span>{event.location || '—'}</span>
              </div>

              <div className="event-card__counts">
                <span>{t('events.mediaCount', { count: event.mediaCount ?? 0 })}</span>
                <span>{t('events.draftCount', { count: event.draftCount ?? 0 })}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
